import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import {
  GuestDetailsForm,
  guestCreatePayloadToApi,
} from "@/components/guests/GuestDetailsForm";
import { api } from "@/lib/apiClient";
import type { Guest, GuestCreatePayload } from "@/types/camping";

function guestToForm(guest: Guest): GuestCreatePayload {
  return {
    guest_type: guest.guest_type,
    title: guest.title ?? "",
    first_name: guest.first_name,
    middle_name: guest.middle_name ?? "",
    last_name: guest.last_name,
    company_name: guest.company_name ?? "",
    vat_id: guest.vat_id ?? "",
    leitweg_id: guest.leitweg_id ?? "",
    date_of_birth: guest.date_of_birth ?? "",
    nationality: guest.nationality ?? "",
    email: guest.email ?? "",
    phone: guest.phone ?? "",
    street: guest.street ?? "",
    house_number: guest.house_number ?? "",
    address_line_2: guest.address_line_2 ?? "",
    postal_code: guest.postal_code ?? "",
    city: guest.city ?? "",
    state_region: guest.state_region ?? "",
    country_code: guest.country_code || "DE",
    notes: guest.notes ?? "",
    is_place_banned: guest.is_place_banned ?? false,
    place_ban_reason: guest.place_ban_reason ?? "",
  };
}

export interface EditGuestModalProps {
  open: boolean;
  guest: Guest | null;
  onClose: () => void;
  onSaved: () => void;
  canEdit: boolean;
}

export function EditGuestModal({ open, guest, onClose, onSaved, canEdit }: EditGuestModalProps) {
  const [form, setForm] = useState<GuestCreatePayload | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !guest) return;
    setForm(guestToForm(guest));
    setError(null);
    setSubmitting(false);
  }, [open, guest]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!guest || !form || !canEdit) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.patch(`/api/v1/guests/${guest.id}`, guestCreatePayloadToApi(form));
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  }

  if (!guest || !form) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${guest.last_name}, ${guest.first_name}`}
      description={canEdit ? "Gastdaten bearbeiten" : "Gastdetails (nur Lesen)"}
      size="xl"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <GuestDetailsForm guest={form} onChange={setForm} disabled={!canEdit} />
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            {canEdit ? "Abbrechen" : "Schließen"}
          </Button>
          {canEdit ? (
            <Button type="submit" loading={submitting}>
              Speichern
            </Button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
